// The external-intelligence document, and the two readings the report takes of
// it.
//
// The shape lives here rather than in IntelPane because two surfaces read it
// now: the Intelligence tab draws the whole table, and the case document's IOC
// matrix marks the indicators an outside source already had a record for. A
// type owned by one pane that another pane imports is a pane other panes depend
// on; this is the document, and both are its readers.
//
// Nothing here renders. Every function is pure and total over a narrowed
// document - JobPanes owns the trust boundary and has already bounded the
// producer's JSON by the time anything below sees it.

/** One provider's answer about one observable. */
export interface IntelSourceResult {
  source: string;
  outcome: 'match' | 'clean' | 'error' | 'skipped';
  error?: string;
  /** Flattened `key: value` facts the provider supplied. Bounded by the
   *  narrower in JobPanes, so nothing downstream walks producer-owned JSON. */
  facts: { key: string; value: string }[];
  /** Fields dropped to keep one record renderable; 0 when nothing was cut. */
  factsOmitted: number;
}

export interface IntelEnrichment {
  kind: string;
  value: string;
  sources: IntelSourceResult[];
}

/** The observable kinds the job collects, and the values its documents carry. */
export type IntelKind = 'hash' | 'host' | 'url';

export const INTEL_KINDS: readonly IntelKind[] = ['hash', 'host', 'url'];

export interface IntelStats {
  observables: number;
  lookups: number;
  matches: number;
  clean: number;
  errors: number;
  skipped: number;
  configured: boolean;
  providerReachable: boolean;
  /** Kinds whose collection cap was reached - the run touched more of each than
   *  were checked. Policy working as configured, not a failure. */
  cappedKinds: IntelKind[];
  /** Collection stopped before the archive ended, so telemetry went unread. */
  scanIncomplete: boolean;
  /**
   * Whether the indicators are the SAMPLE's rather than the whole machine's.
   *
   * A capture is very nearly the whole box - the kernel driver's rundown
   * watches everything already alive - and the archive's promotion reasons are
   * what separate the two. `false` means it carried none, so the table also
   * covers Windows' own background traffic, which reads exactly like the
   * sample's infrastructure unless the pane says otherwise. `null` is a
   * document written before the job scoped at all, which cannot make either
   * claim.
   */
  scopeResolved: boolean | null;
  /** Set only for documents produced before the job separated the two facts
   *  above, which carried one flag meaning either. Their reports can say that
   *  coverage fell short but not which way, and inventing the distinction for
   *  them would be worse than saying less. */
  legacyTruncated: boolean;
  deadlineHit: boolean;
  collectionErrors: number;
}

export interface IntelDocument {
  provider: string;
  sampleSha256: string;
  enrichments: IntelEnrichment[];
  stats: IntelStats;
}

// Narrowing the producer's JSON is the TRUST BOUNDARY, and it lives here
// because two surfaces cross it: the report's Intelligence tab reads a stored
// spamhaus document, and the submit page's search reads a live lookup. Both
// receive the same provider records over the same four outcomes, so one
// narrower is what keeps them agreeing about what a record says.

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

// Bounds on one provider record. `detail` is producer-owned JSON whose depth
// and width nothing upstream constrains for rendering, and a record that
// exceeds either is elided VISIBLY - a silently short list reads as a short
// record rather than as a clamped one.
const MAX_DETAIL_DEPTH = 4;
// Generous rather than tight: the bound stops a hostile record rendering
// unboundedly, it does not curate. One provider answer alone carries over
// twenty fields, and the search panel shows a merged record whole.
export const MAX_DETAIL_FACTS = 96;

/** Flatten one provider record into displayable `key: value` facts.
 *
 *  Nested objects contribute dotted keys; arrays of scalars join into one
 *  value. Anything past the depth bound is dropped rather than rendered as
 *  "[object Object]".
 *
 *  A list of RECORDS is the ThreatFox case - it answers with one entry per IOC
 *  report - and a single entry contributes its fields unprefixed, because
 *  "0.threat type" is an artefact of the wire shape rather than something a
 *  reader asked to see. Several entries are numbered from one, so two reports'
 *  fields stay distinguishable.
 */
function asFacts(value: unknown, prefix = '', depth = 0): { key: string; value: string }[] {
  if (depth >= MAX_DETAIL_DEPTH || value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    const scalars = value.filter((v) => typeof v !== 'object' || v === null).map(String);
    if (scalars.length > 0) return [{ key: prefix, value: scalars.join(', ') }];
    if (value.length === 1) return asFacts(value[0], prefix, depth);
    return value.flatMap((v, i) =>
      asFacts(v, prefix ? `${prefix}.#${i + 1}` : `#${i + 1}`, depth + 1),
    );
  }
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).flatMap(([k, v]) =>
      asFacts(v, prefix ? `${prefix}.${k}` : k, depth + 1),
    );
  }
  const text = String(value);
  return text === '' ? [] : [{ key: prefix, value: text }];
}

const INTEL_OUTCOMES = new Set(['match', 'clean', 'error', 'skipped']);

export function asIntelSource(value: unknown): IntelSourceResult | null {
  if (typeof value !== 'object' || value === null) return null;
  const row = value as Record<string, unknown>;
  const source = str(row.source);
  const outcome = str(row.outcome);
  // An outcome this UI does not know is dropped rather than guessed at. The
  // four states are the contract, and inventing a fifth reading here is how a
  // renderer starts disagreeing with the document.
  if (source === '' || !INTEL_OUTCOMES.has(outcome)) return null;
  const facts = asFacts(row.detail);
  return {
    source,
    outcome: outcome as IntelSourceResult['outcome'],
    error: str(row.error),
    facts: facts.slice(0, MAX_DETAIL_FACTS),
    factsOmitted: Math.max(0, facts.length - MAX_DETAIL_FACTS),
  };
}

/**
 * The four outcomes in words, for every surface that renders one.
 *
 * Sentence case, and FOUR of them. "No record" is a result; "Not checked" is
 * the absence of one, and a reader who cannot tell them apart will read an
 * outage as a clean bill of health. Two surfaces show these - the report's
 * Intelligence tab and the submit page's search - and one of them disagreeing
 * about the wording is one of them making a different claim.
 */
export const INTEL_OUTCOME_LABELS: Record<IntelSourceResult['outcome'], string> = {
  match: 'Reported',
  clean: 'No record',
  error: 'Lookup failed',
  skipped: 'Not checked',
};

// The datasets, named as their publishers name them. A source column reading
// `urlhaus_host` makes the reader do the translation.
const SOURCE_LABELS: Record<string, string> = {
  threatfox: 'ThreatFox',
  malwarebazaar: 'MalwareBazaar',
  urlhaus_host: 'URLhaus (host)',
  urlhaus_url: 'URLhaus (URL)',
  urlhaus_payload: 'URLhaus (payload)',
  yaraify: 'YARAify',
};

/** A source's published name, or its wire id when the platform gained a source
 *  this build does not know about. Never blank - an unlabelled cell reads as a
 *  rendering fault rather than as a source nobody has named yet. */
export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

/**
 * One indicator's answer, folded from every source asked about it.
 *
 * Four values, not a found flag, and the order below is the precedence: a
 * record beats a clean lookup, a clean lookup beats a failed one, and a failure
 * beats a lookup nobody made. The distinction that carries the weight is
 * `clean` against `skipped` - the first is a real if bounded negative result,
 * the second says NOTHING about the indicator, and collapsing them is how a
 * provider outage renders as a clean bill of health.
 */
export type IntelRowVerdict = 'reported' | 'clean' | 'error' | 'skipped';

/** One row of the Intelligence table: an indicator and what came back. */
export interface IntelRow {
  kind: string;
  value: string;
  sources: IntelSourceResult[];
  /** Published names of the sources that returned a record, in source order and
   *  each named once - a provider may answer with several records for one
   *  observable, and "ThreatFox, ThreatFox" reads as two sources. */
  matchedSources: string[];
  verdict: IntelRowVerdict;
  /** The provider's own words for what it holds - a family, a signature, a
   *  threat class. Null when it matched but named nothing, and on every
   *  non-match. */
  threatLabel: string | null;
}

// The fields a provider states its own verdict in, most specific first.
//
// Matched by SUFFIX, not equality: the narrower prefixes the fields of a
// multi-record answer with `#1.`, `#2.` and so on, so ThreatFox's second report
// arrives as `#2.malware_printable`. Comparing whole keys silently finds
// nothing on exactly the records that carry the most.
const THREAT_KEYS = [
  'malware_printable',
  'malware',
  'signature',
  'threat',
  'threat_type',
] as const;

function factBySuffix(source: IntelSourceResult, key: string): string | null {
  for (const fact of source.facts) {
    if (fact.key === key || fact.key.endsWith(`.${key}`)) {
      const value = fact.value.trim();
      if (value !== '') return value;
    }
  }
  return null;
}

/**
 * What the sources that matched actually said, as one short label.
 *
 * At most two values and never repeated: abuse.ch's feeds cross-reference each
 * other, so a URL matched by URLhaus and ThreatFox routinely names the same
 * family twice, and "NightshadeC2 · NightshadeC2" reads as two findings.
 */
export function threatLabelOf(sources: readonly IntelSourceResult[]): string | null {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const source of sources) {
    if (source.outcome !== 'match') continue;
    for (const key of THREAT_KEYS) {
      const value = factBySuffix(source, key);
      if (!value) continue;
      const dedupe = value.toLowerCase();
      if (seen.has(dedupe)) break;
      seen.add(dedupe);
      parts.push(value);
      break;
    }
    if (parts.length === 2) break;
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** The sources that returned a record, named once each, in source order. */
function matchedSourcesOf(sources: readonly IntelSourceResult[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const source of sources) {
    if (source.outcome !== 'match') continue;
    const label = sourceLabel(source.source);
    if (seen.has(label)) continue;
    seen.add(label);
    out.push(label);
  }
  return out;
}

function verdictOf(sources: readonly IntelSourceResult[]): IntelRowVerdict {
  if (sources.some((s) => s.outcome === 'match')) return 'reported';
  if (sources.some((s) => s.outcome === 'clean')) return 'clean';
  if (sources.some((s) => s.outcome === 'error')) return 'error';
  return 'skipped';
}

/**
 * The document as one table, records first.
 *
 * Sorted rather than left in document order because the answer the pane exists
 * to give is "which of these did somebody else already know about", and on a
 * run with thirty-six observables and two records, document order buries both.
 * Within each group the document's order is kept - it is collection order, and
 * reordering it would lose the only sequence the run actually recorded.
 */
export function intelRows(document: IntelDocument): IntelRow[] {
  return document.enrichments
    .map((enrichment) => ({
      kind: enrichment.kind,
      value: enrichment.value,
      sources: enrichment.sources,
      matchedSources: matchedSourcesOf(enrichment.sources),
      verdict: verdictOf(enrichment.sources),
      threatLabel: threatLabelOf(enrichment.sources),
    }))
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const lead = Number(b.row.verdict === 'reported') - Number(a.row.verdict === 'reported');
      return lead !== 0 ? lead : a.i - b.i;
    })
    .map(({ row }) => row);
}

/**
 * Indicator value → the sources that hold a record for it, for the case
 * document's IOC matrix.
 *
 * Keyed on the lower-cased value because the two sides are normalised
 * differently: the spamhaus job lower-cases hosts and hashes at collection time
 * (see voltz-core/threatintel/observable.go), while the analyst tier writes IOC values
 * however it read them. Folding case is what makes a host the model wrote as
 * `C2.Example.Test` meet the `c2.example.test` the document carries.
 *
 * It is an exact-value join and does not try to be more. A URL the model quoted
 * with a fragment the job stripped will not match, and that costs a marker on
 * one row rather than a wrong answer anywhere - nothing here decides a verdict.
 */
export function enrichedByIndex(document: IntelDocument): ReadonlyMap<string, string[]> {
  const index = new Map<string, string[]>();
  for (const enrichment of document.enrichments) {
    const matched = matchedSourcesOf(enrichment.sources);
    if (matched.length === 0) continue;
    const key = enrichment.value.trim().toLowerCase();
    if (key === '') continue;
    // The job can collect the same value under two kinds - a host and the URL
    // it appears in both normalise to their own row - so merge rather than
    // overwrite, and keep each source once.
    const existing = index.get(key);
    index.set(key, existing ? [...new Set([...existing, ...matched])] : matched);
  }
  return index;
}
